import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { FormErrorBanner } from '../shared/FormErrorBanner';
import { Download, Upload, FileText, BookOpen } from 'lucide-react';
import type { CarrierImportReport } from '../../types/companies';

// Carrier Directory import modal (issue #178). The modal renders the
// three actions the acceptance criteria list — Download CSV Template,
// CSV file upload, and submit — and surfaces the row-level report
// returned by the backend without any follow-up round-trip. All
// business logic (file selection, mutation, cache invalidation) lives
// in the parent hook; this component only renders props and calls
// handlers, mirroring the surrounding `CompanyFormModal` style.
export interface CarrierImportModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  report: CarrierImportReport | null;
  errorMessage: string | null;
  submitting: boolean;
  templateDownloading: boolean;
  onDownloadTemplate: () => void;
  guideDownloading: boolean;
  onDownloadGuide: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const CarrierImportModal: React.FC<CarrierImportModalProps> = ({
  isOpen,
  onOpenChange,
  file,
  onFileChange,
  report,
  errorMessage,
  submitting,
  templateDownloading,
  onDownloadTemplate,
  guideDownloading,
  onDownloadGuide,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  // The native file input is the only way to keep the
  // "re-select the same file" UX correct; we clear its value whenever
  // a new import flow starts or the parent resets the selected file.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (file === null && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [file]);

  // Stable localized labels so the row-level report renders the
  // backend's `reason` keys in the admin's language without leaking
  // the raw i18n keys to the UI.
  const reasonLabel = (reason: string | null) => {
    if (!reason) {
      return '';
    }
    return t(`admin.companies.import.reasons.${reason}`, reason);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    onFileChange(next);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
        onOpenChange(open);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('admin.companies.importTitle')}</DialogTitle>
          <DialogDescription>
            {t('admin.companies.importDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-2 rounded-md border bg-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2">
              <FileText className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div className="space-y-0.5">
                <p className="text-sm font-medium">
                  {t('admin.companies.importTemplateLabel')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('admin.companies.importTemplateHelp')}
                </p>
                <button
                  type="button"
                  onClick={onDownloadGuide}
                  disabled={guideDownloading}
                  aria-busy={guideDownloading}
                  className="inline-flex items-center gap-1.5 py-1 text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
                >
                  <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  {guideDownloading
                    ? t('admin.companies.importGuideDownloading')
                    : t('admin.companies.importDownloadGuide')}
                </button>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={onDownloadTemplate}
              disabled={templateDownloading}
            >
              <Download className="h-4 w-4 mr-2" />
              {templateDownloading
                ? t('admin.companies.importTemplateDownloading')
                : t('admin.companies.importDownloadTemplate')}
            </Button>
          </div>

          <form
            className="space-y-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <div className="space-y-2">
              <label htmlFor="carrier-import-file" className="text-sm font-medium">
                {t('admin.companies.importFileLabel')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  id="carrier-import-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileInputChange}
                  disabled={submitting}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-sm file:font-medium file:text-slate-900 hover:file:bg-slate-50"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.companies.importFileHelp')}
              </p>
            </div>

            {/* Pre-flight errors (e.g. non-CSV uploads, network
                failures) bubble through the hook as a single string
                and render in the shared banner. The row-level report
                below covers in-flight rejections and is the
                "success" surface for partial-success imports. */}
            <FormErrorBanner errors={errorMessage ? [errorMessage] : []} />

            {report && (
              <div
                role="status"
                aria-live="polite"
                className="rounded-md border bg-white p-4 space-y-3"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t('admin.companies.importReportTitle')}
                  </p>
                  <p className="text-sm text-muted-foreground">{report.message}</p>
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-emerald-700">
                    {t('admin.companies.importCreatedCount', {
                      count: report.created_count,
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-amber-700">
                    {t('admin.companies.importSkippedCount', {
                      count: report.skipped_count,
                    })}
                  </span>
                </div>
                {report.skipped_count > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">
                      {t('admin.companies.importSkippedDetails')}
                    </p>
                    <ul className="space-y-1 text-sm">
                      {report.rows
                        .filter((row) => row.status === 'skipped')
                        .map((row) => (
                          <li
                            key={`${row.row_number}-${row.reason ?? 'unknown'}`}
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
                          >
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="font-medium">
                                {t('admin.companies.importRowLabel', {
                                  row: row.row_number,
                                })}
                              </span>
                              {row.reason && (
                                <span className="text-xs uppercase tracking-wide text-amber-700">
                                  {reasonLabel(row.reason)}
                                </span>
                              )}
                            </div>
                            {row.message && (
                              <p className="text-xs text-muted-foreground">
                                {row.message}
                              </p>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting}
              >
                {report ? t('common.close') : t('common.cancel')}
              </Button>
              {!report && (
                <Button
                  type="submit"
                  disabled={!file || submitting}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {submitting
                    ? t('admin.companies.importSubmitting')
                    : t('admin.companies.importSubmit')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
